import React, { useState } from "react";
import { BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money, fmtDate } from "../lib/api";
import { useLang } from "../lib/i18n";
import { printDoc } from "../lib/printDoc";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export function SaleResults() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const load = (f = from, tt = to) => API.get("/shop/auctions/results", { params: { date_from: f || undefined, date_to: tt || undefined } })
    .then((r) => setData(r.data)).catch((err) => toast.error(fmtErr(err)));
  const tot = data?.totals;
  return (
    <>
      <Button variant="outline" data-testid="sale-results-button" onClick={() => { setOpen(true); load(); }}>
        <BarChart3 className="h-4 w-4 mr-1.5" />{t("res.button")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="sale-results-dialog">
          <DialogHeader><DialogTitle className="font-heading">{t("res.title")}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div><Label className="text-xs">{t("res.from")}</Label><Input type="date" data-testid="results-from" value={from} onChange={(e) => { setFrom(e.target.value); load(e.target.value, to); }} className="mt-1 h-9" /></div>
            <div><Label className="text-xs">{t("res.to")}</Label><Input type="date" data-testid="results-to" value={to} onChange={(e) => { setTo(e.target.value); load(from, e.target.value); }} className="mt-1 h-9" /></div>
            <div className="flex-1" />
            <Button data-testid="results-print-button" disabled={!data?.rows.length} className="bg-slate-900 hover:bg-slate-800 text-white h-9"
              onClick={() => printDoc(document.querySelector('[data-testid="results-body"]'), { mode: "a4", title: "Sale results" })}>{t("repay.print")}</Button>
          </div>
          {data && (
            <div className="print-area bg-white text-sm" data-testid="results-body">
              <div className="border-b-2 border-[var(--brand,#1E3A5F)] pb-2 mb-3 flex justify-between items-end">
                <div><div className="font-heading font-bold text-lg text-[var(--brand,#1E3A5F)] uppercase">{data.shop.name}</div><div className="text-xs text-slate-500">{t("res.title")} · {from || "…"} → {to || "…"}</div></div>
                <div className="text-xs font-mono text-slate-500">{data.generated_at.slice(0, 10)}</div>
              </div>
              {data.rows.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center" data-testid="results-empty">{t("res.empty")}</p> : (
                <table className="w-full text-xs">
                  <thead><tr className="border-b-2 border-slate-800 text-left">
                    <th className="py-1.5 pl-1">{t("common.date")}</th><th>{t("ann.lot")}</th><th>{t("loans.number")}</th><th>{t("inv.buyer")}</th>
                    <th className="text-right">{t("ann.est")}</th><th className="text-right">{t("ann.reserve")}</th><th className="text-right">{t("res.winning_bid")}</th>
                    <th className="text-right">{t("inv.total_debt_short")}</th><th className="text-right">{t("inv.surplus")}</th><th className="text-right">{t("inv.loss")}</th><th className="text-right pr-1">{t("inv.profit")}</th>
                  </tr></thead>
                  <tbody>
                    {data.rows.map((r) => (
                      <tr key={r.sale_id} className="border-b border-slate-100" data-testid={`results-row-${r.invoice_no}`}>
                        <td className="py-1.5 pl-1 font-mono">{fmtDate(r.sold_at)}</td>
                        <td>{r.item_name}<div className="text-slate-400">{r.category}</div></td>
                        <td className="font-mono">{r.loan_number || "—"}</td>
                        <td>{r.buyer_name || "—"}{r.bids_count ? <div className="text-slate-400">{r.bids_count} {t("res.bids")}</div> : null}</td>
                        <td className="text-right font-mono">{money(r.estimated_value)}</td>
                        <td className="text-right font-mono">{r.reserve_price != null ? money(r.reserve_price) : "—"}</td>
                        <td className="text-right font-mono font-semibold">{money(r.winning_bid ?? r.sale_price)}</td>
                        <td className="text-right font-mono">{money(r.total_due)}</td>
                        <td className="text-right font-mono text-emerald-700">{r.surplus ? money(r.surplus) : "—"}</td>
                        <td className="text-right font-mono text-red-700">{r.loss ? money(r.loss) : "—"}</td>
                        <td className="text-right font-mono pr-1">{money(r.profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr className="border-t-2 border-slate-800 font-bold" data-testid="results-totals">
                    <td className="py-2 pl-1" colSpan={4}>{t("res.totals")} · {tot.count} {t("res.lots")}</td>
                    <td className="text-right font-mono">{money(tot.estimated_value)}</td><td />
                    <td className="text-right font-mono">{money(tot.sale_price)}</td>
                    <td className="text-right font-mono">{money(tot.total_due)}</td>
                    <td className="text-right font-mono text-emerald-700">{money(tot.surplus)}</td>
                    <td className="text-right font-mono text-red-700">{money(tot.loss)}</td>
                    <td className="text-right font-mono pr-1">{money(tot.profit)}</td>
                  </tr></tfoot>
                </table>
              )}
              <p className="text-[10px] text-slate-500 mt-3">{t("res.note")}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
